import { forwardRef, type JSX } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, KeyRound, FlaskConical, ServerCog,
  GitPullRequest, TrendingUp, AlertTriangle, Bug, DollarSign,
  ShoppingCart, FileText, Shield, Cpu, Rocket, MessageSquare,
  Gauge, SlidersHorizontal, Mail, X, CreditCard, type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useSession } from '../../session';
import { useNewContactCount } from './contactBadge';

/**
 * Sidebar — ported from agent-system's components/sidebar.tsx.
 * Brand badge with gradient, nav links with per-link icon color,
 * pill active state, avatar circle, sign-out button.
 */

interface NavLink {
  href: string;
  label: string;
  icon: LucideIcon;
  color: string;   // icon tint when not-active
  /** Owner / admin only (the page and its API refuse everyone else). */
  superadminOnly?: boolean;
}

const PRIMARY_LINKS: NavLink[] = [
  { href: '/',                        label: 'Dashboard',    icon: LayoutDashboard, color: '#60a5fa' }, // blue-400
  { href: '/customers',               label: 'Customers',    icon: Users,           color: '#c084fc' }, // purple-400
  { href: '/licenses',                label: 'Licenses',     icon: KeyRound,        color: '#22d3ee' }, // cyan-400
  { href: '/reports/capacity',        label: 'Capacity',     icon: Gauge,           color: '#a3e635' }, // lime-400
  { href: '/trials',                  label: 'Trials',       icon: FlaskConical,    color: '#fb923c' }, // orange-400
  { href: '/servers',                 label: 'Servers',      icon: ServerCog,       color: '#34d399' }, // emerald-400
  { href: '/engines',                 label: 'Engines',      icon: Cpu,             color: '#38bdf8' }, // sky-400
  { href: '/funnel',                  label: 'Funnel',       icon: GitPullRequest,  color: '#818cf8' }, // indigo-400
  { href: '/analytics',               label: 'Analytics',    icon: TrendingUp,      color: '#2dd4bf' }, // teal-400
];

const OPS_LINKS: NavLink[] = [
  { href: '/anomalies',               label: 'Anomalies',    icon: AlertTriangle,   color: '#fbbf24' }, // amber-400
  { href: '/tickets',                 label: 'Tickets',      icon: MessageSquare,   color: '#60a5fa' }, // blue-400
  { href: '/platform-bugs',           label: 'Platform bugs', icon: Bug,            color: '#f87171' }, // red-400
  { href: '/contact-messages',        label: 'Contact messages', icon: Mail,        color: '#38bdf8', superadminOnly: true }, // sky-400
  { href: '/updates',                 label: 'Engine updates', icon: Rocket,        color: '#a78bfa' }, // violet-400
  { href: '/customer-certs',          label: 'Customer certs', icon: KeyRound,      color: '#22d3ee' }, // cyan-400
  { href: '/pricing',                 label: 'Plans',        icon: DollarSign,      color: '#4ade80' }, // green-400
  { href: '/stripe',                  label: 'Stripe',       icon: CreditCard,      color: '#818cf8' }, // indigo-400
  { href: '/marketplace-entitlements', label: 'Marketplace', icon: ShoppingCart,    color: '#f472b6' }, // pink-400
];

const ADMIN_LINKS: NavLink[] = [
  { href: '/audit-log',               label: 'Audit log',    icon: FileText,        color: '#fb7185' }, // rose-400
  { href: '/staff',                   label: 'Staff',        icon: Shield,          color: '#facc15' }, // yellow-400
  { href: '/settings',                label: 'Settings',     icon: SlidersHorizontal, color: '#94a3b8' }, // slate-400
];

export const SIDEBAR_ID = 'admin-sidebar';

function NavItem({ link, badge, onNavigate }: { link: NavLink; badge?: number | null; onNavigate?: () => void }): JSX.Element {
  const loc = useLocation();
  const isActive =
    loc.pathname === link.href ||
    (link.href !== '/' && loc.pathname.startsWith(link.href));
  const Icon = link.icon;

  return (
    <Link
      to={link.href}
      className={clsx('sidebar__link', isActive && 'sidebar__link--active')}
      aria-current={isActive ? 'page' : undefined}
      onClick={onNavigate}
    >
      <Icon aria-hidden="true" style={{ color: isActive ? 'var(--accent)' : link.color }} />
      <span>{link.label}</span>
      {badge ? (
        <span className="sidebar__badge" aria-label={`${badge} new`}>{badge > 99 ? '99+' : badge}</span>
      ) : (
        isActive && <span className="sidebar__link-dot" />
      )}
    </Link>
  );
}

export interface SidebarProps {
  /** Narrow layout: the sidebar is an off-canvas drawer. */
  mobile?: boolean;
  /** Drawer is open (only meaningful when `mobile`). */
  open?: boolean;
  onClose?: () => void;
  /** Called when a nav link is activated (lets the shell close the drawer). */
  onNavigate?: () => void;
}

export const Sidebar = forwardRef<HTMLElement, SidebarProps>(function Sidebar(
  { mobile = false, open = false, onClose, onNavigate },
  ref,
): JSX.Element {
  const { state } = useSession();
  const isSuperadmin = (state.user?.role === 'owner' || state.user?.role === 'admin') && state.user?.mfa_ok === true;
  const newContact = useNewContactCount(isSuperadmin ? state.token : null);
  const visible = (l: NavLink): boolean => !l.superadminOnly || isSuperadmin;
  // Theme toggle, notifications, and the user/sign-out controls live in
  // the TopBar (see components/shell/TopBar.tsx). The sidebar is a pure
  // brand + nav surface; on narrow screens it is an off-canvas drawer.
  const drawerProps = mobile
    ? open
      ? { role: 'dialog' as const, 'aria-modal': true, 'aria-label': 'Navigation menu' }
      : { inert: true }
    : {};
  return (
    <aside
      ref={ref}
      id={SIDEBAR_ID}
      className={clsx('sidebar', mobile && 'sidebar--drawer', open && 'sidebar--open')}
      data-open={open ? 'true' : 'false'}
      {...drawerProps}
    >
      <div className="sidebar__brand">
        <div className="sidebar__brand-left">
          <img className="sidebar__brand-logo" src="/vloud-mark.png" alt="" aria-hidden="true" />
          <div className="sidebar__brand-text">
            <h1>Vloud Admin</h1>
            <p>Licensing &amp; Operations</p>
          </div>
        </div>
        {mobile && (
          <button
            type="button"
            className="sidebar__close"
            aria-label="Close navigation menu"
            onClick={onClose}
          >
            <X width={18} height={18} aria-hidden="true" />
          </button>
        )}
      </div>

      <nav className="sidebar__nav" aria-label="Primary">
        <div className="sidebar__section-title">Overview</div>
        {PRIMARY_LINKS.map((l) => <NavItem key={l.href} link={l} onNavigate={onNavigate} />)}

        <div className="sidebar__section-title" style={{ marginTop: 8 }}>Operations</div>
        {OPS_LINKS.filter(visible).map((l) => (
          <NavItem key={l.href} link={l} badge={l.href === '/contact-messages' ? newContact : null} onNavigate={onNavigate} />
        ))}

        <div className="sidebar__section-title" style={{ marginTop: 8 }}>Admin</div>
        {ADMIN_LINKS.map((l) => <NavItem key={l.href} link={l} onNavigate={onNavigate} />)}
      </nav>
    </aside>
  );
});
